/**
 * MonthStreakCalendar — the tap-to-expand month-streak overlay.
 *
 * iOS source of truth: client-native/src/features/home/components/
 * WeeklyCalendar.tsx:312-505 (the calendar overlay that fades in over the hero
 * when the streak pill / week strip is tapped). Ports, in order:
 *   - "YOUR STREAK" summary with fire + day count  (WeeklyCalendar.tsx:314-333)
 *   - navigable month grid, back limited to 24 months (:205-213, :336-374)
 *   - "Today" reset pill                            (:377-400)
 *   - Sunday-first weekday header                   (:402-415)
 *   - month grid: join-date flag + "Joined" tooltip, before-join dimming,
 *     today/completed/empty/future states           (:417-481)
 *   - "Close Calendar" bar                          (:483-504)
 *
 * The web has no in-hero drawer to expand into, so this renders as a fixed
 * full-screen modal over the same blue gradient the iOS hero uses. Streak math
 * is shared with the home strip via streakUtils.computeMonthWeeks.
 */
import { FC, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { colors } from "@/ui";
import { callWithAuth, getStreakDays } from "@/api/Api";
import { fireIcon } from "@/components/Profile/profileAssets";
import SkyBackground from "@/components/Home/SkyBackground";
import { computeMonthWeeks } from "./streakUtils";
import type { DayActivity, MonthDay } from "./streakUtils";

export interface MonthStreakCalendarProps {
  /** Consecutive-day streak (already floored to >=1 by the caller). */
  dayStreak: number;
  /** Account creation date (GetUserInfoResponse.joinDate), ISO string. */
  joinDate?: string;
  /** Whether the user id is known yet (gates the streak-days fetch). */
  userId?: number;
  onClose: () => void;
}

/** YYYY-MM-DD in local time (matches streakUtils.localDateKey). */
function fmtDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Same check glyph the strip uses (StreakDayCircle.CheckmarkWhite). */
function CheckmarkWhite({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      <path
        d="M5 12.5l4.2 4.2L19 7"
        fill="none"
        stroke="#ffffff"
        strokeWidth={2.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Chevron({ dir, size = 20 }: { dir: "left" | "right" | "up"; size?: number }) {
  const d =
    dir === "left"
      ? "M15 6l-6 6 6 6"
      : dir === "right"
        ? "M9 6l6 6-6 6"
        : "M6 15l6-6 6 6";
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      <path
        d={d}
        fill="none"
        stroke={colors.white}
        strokeWidth={2.2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const WEEKDAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"]; // Sunday-first (iOS :403)

const MonthStreakCalendar: FC<MonthStreakCalendarProps> = ({
  dayStreak,
  joinDate: joinDateStr,
  userId,
  onClose,
}) => {
  const today = useMemo(() => new Date(), []);
  const [monthOffset, setMonthOffset] = useState(0);
  const [showJoinTooltip, setShowJoinTooltip] = useState(false);

  const joinDateParsed = useMemo(() => {
    if (!joinDateStr) return undefined;
    const d = new Date(joinDateStr);
    return isNaN(d.getTime()) ? undefined : d;
  }, [joinDateStr]);

  // 24-month back window for the navigable grid (iOS keeps a 2-year MMKV cache;
  // here we fetch the same span once the overlay is open). Mirrors
  // useStreakDays' mapping of each returned day to { date, value: 1 }.
  const windowStart = useMemo(() => {
    const d = new Date(today.getFullYear(), today.getMonth() - 24, 1);
    return fmtDate(d);
  }, [today]);
  const windowEnd = useMemo(() => fmtDate(today), [today]);

  const streakQuery = useQuery({
    queryKey: ["streakDays", userId, windowStart, windowEnd],
    queryFn: () => callWithAuth(getStreakDays, { startDate: windowStart, endDate: windowEnd }),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // iOS parity (5 min)
  });
  const weeklyData: DayActivity[] = useMemo(
    () => (streakQuery.data?.data ?? []).map((s) => ({ date: s.date, value: 1 })),
    [streakQuery.data],
  );

  const viewDate = useMemo(() => {
    if (monthOffset === 0) return today;
    return new Date(today.getFullYear(), today.getMonth() + monthOffset, 1, 12);
  }, [today, monthOffset]);

  const monthWeeks = useMemo(
    () => computeMonthWeeks(viewDate, today, dayStreak, weeklyData, joinDateParsed),
    [viewDate, today, dayStreak, weeklyData, joinDateParsed],
  );

  // Browse back up to 24 months regardless of join-date edge cases (iOS
  // WeeklyCalendar.tsx:205 — monthOffset > -24, NOT the joinDate clamp).
  const canGoBack = monthOffset > -24;
  const canGoForward = monthOffset < 0;

  const monthLabel = useMemo(
    () => viewDate.toLocaleString("default", { month: "long", year: "numeric" }),
    [viewDate],
  );

  // ── per-cell circle visuals (port of WeeklyCalendar.tsx:441-475 styles) ──
  function cellVisual(day: MonthDay) {
    let background = "transparent";
    let border = "none";
    let opacity = 1;
    let boxShadow = "none";
    if (day.isJoinDate || (day.isToday && !day.isJoinDate)) {
      background = colors.white;
      boxShadow = "0 2px 6px rgba(0,0,0,0.15)";
    } else if (day.completed && !day.isToday && !day.isBeforeJoin) {
      background = colors.whiteMedium;
    } else if (
      !day.completed &&
      !day.isToday &&
      !day.isFuture &&
      !day.isBeforeJoin &&
      !day.isJoinDate
    ) {
      border = `1.5px dashed ${colors.whiteMedium}`;
      opacity = 0.45; // iOS DAY_OPACITY.inactive (missed/empty past day)
    }
    if (day.isBeforeJoin) opacity = 0.45; // iOS DAY_OPACITY.inactive
    else if (day.isFuture) opacity = 0.6; // iOS DAY_OPACITY.future
    return { background, border, opacity, boxShadow };
  }

  const navArrowStyle: React.CSSProperties = {
    width: 36,
    height: 36,
    borderRadius: 18,
    background: colors.whiteSubtle,
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 0,
  };

  return (
    <div
      role="dialog"
      aria-label="Your streak calendar"
      style={{
        position: "fixed",
        inset: 0,
        // Below the fixed top header (parrot logo / streak pill) and the bottom
        // nav — both z-10 — so they stay visible OVER the calendar. iOS keeps the
        // AppTopBar + tab bar while the calendar fades in over the sky.
        zIndex: 9,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        overflowY: "auto",
        // Top pad clears the 64px header strip; bottom pad clears the floating nav.
        padding: "80px 16px 120px",
        boxSizing: "border-box",
      }}
    >
      {/* The real home sky (gradient + spinning sun + drifting clouds) behind the
          calendar, so the clouds/sun stay visible instead of a flat opaque fill
          that hid them (iOS fades the calendar in over the live sky). */}
      <SkyBackground />
      <div
        style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 380 }}
      >
        {/* YOUR STREAK summary */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 8,
            margin: "16px 0",
          }}
        >
          <div
            style={{
              fontSize: "var(--base-font-size-xs)",
              fontWeight: 600,
              color: colors.white,
              letterSpacing: 1,
            }}
          >
            YOUR STREAK
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <img
              src={fireIcon}
              alt=""
              aria-hidden
              style={{ width: 40, height: 40, objectFit: "contain" }}
            />
            <span
              style={{
                // iOS WeeklyCalendar streak headline is variant="xxxlarge"
                // bold. `--base-font-size-xxxlarge` is undefined in App.css
                // (only the numbered -3xlarge exists), which silently fell back
                // to the inherited ~16px. -3xlarge (28px default) is the web
                // mapping used for the xxxlarge-equivalent headline.
                fontSize: "var(--base-font-size-3xlarge)",
                fontWeight: 700,
                color: colors.white,
              }}
            >
              {dayStreak} {dayStreak === 1 ? "day" : "days"}
            </span>
          </div>
        </div>

        {/* Month navigation row */}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 16,
            marginBottom: 8,
          }}
        >
          {canGoBack ? (
            <button
              type="button"
              aria-label="Previous month"
              onClick={() => setMonthOffset((o) => o - 1)}
              style={navArrowStyle}
            >
              <Chevron dir="left" />
            </button>
          ) : (
            <div style={{ width: 36, height: 36 }} />
          )}
          <div
            style={{
              minWidth: 160,
              textAlign: "center",
              // iOS month label is variant="base" bold. `-base` is not a
              // defined var (the base size is the bare `--base-font-size`).
              fontSize: "var(--base-font-size)",
              fontWeight: 700,
              color: colors.white,
            }}
          >
            {monthLabel}
          </div>
          {canGoForward ? (
            <button
              type="button"
              aria-label="Next month"
              onClick={() => setMonthOffset((o) => o + 1)}
              style={navArrowStyle}
            >
              <Chevron dir="right" />
            </button>
          ) : (
            <div style={{ width: 36, height: 36 }} />
          )}
        </div>

        {/* "Today" reset pill */}
        {monthOffset !== 0 && (
          <button
            type="button"
            onClick={() => setMonthOffset(0)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              margin: "0 auto 12px",
              padding: "6px 14px",
              borderRadius: 14,
              background: colors.whiteSubtle,
              border: "none",
              cursor: "pointer",
              color: colors.white,
            }}
          >
            <svg width={14} height={14} viewBox="0 0 24 24" aria-hidden>
              <path
                d="M9 10h6a4 4 0 0 1 0 8h-2M9 10l-3 3m3-3l-3-3"
                fill="none"
                stroke={colors.white}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span style={{ fontSize: "var(--base-font-size-xs)", fontWeight: 600 }}>
              Today
            </span>
          </button>
        )}

        {/* Weekday header (Sunday-first) */}
        <div style={{ display: "flex", flexDirection: "row", marginBottom: 8 }}>
          {WEEKDAY_LABELS.map((lbl, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                textAlign: "center",
                fontSize: "var(--base-font-size-xs)",
                fontWeight: 700,
                color: colors.white,
                opacity: 0.75, // iOS DAY_OPACITY.dayLabels
              }}
            >
              {lbl}
            </div>
          ))}
        </div>

        {/* Month grid */}
        {monthWeeks.map((week, wi) => (
          <div
            key={wi}
            style={{ display: "flex", flexDirection: "row", marginBottom: 8 }}
          >
            {week.map((day, di) => {
              if (day.dayNumber === null) {
                return <div key={di} style={{ flex: 1, height: 40 }} />;
              }
              const v = cellVisual(day);
              const showCheck =
                day.completed && !day.isToday && !day.isBeforeJoin && !day.isJoinDate;
              return (
                <div
                  key={di}
                  style={{
                    flex: 1,
                    height: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                  }}
                >
                  {day.isJoinDate && showJoinTooltip && (
                    <div
                      style={{
                        position: "absolute",
                        top: -24,
                        left: "50%",
                        transform: "translateX(-50%)",
                        background: colors.blackOverlay,
                        padding: "3px 8px",
                        borderRadius: 6,
                        zIndex: 10,
                        whiteSpace: "nowrap",
                        fontSize: "var(--base-font-size-xs)",
                        fontWeight: 600,
                        color: colors.white,
                      }}
                    >
                      Joined
                    </div>
                  )}
                  <button
                    type="button"
                    disabled={!day.isJoinDate}
                    aria-label={day.isJoinDate ? "Join date" : undefined}
                    onClick={
                      day.isJoinDate
                        ? () => setShowJoinTooltip((s) => !s)
                        : undefined
                    }
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: "50%",
                      background: v.background,
                      border: v.border,
                      opacity: v.opacity,
                      boxShadow: v.boxShadow,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 0,
                      cursor: day.isJoinDate ? "pointer" : "default",
                    }}
                  >
                    {day.isJoinDate ? (
                      <svg width={20} height={20} viewBox="0 0 24 24" aria-hidden>
                        <path
                          d="M5 3v18M5 4h11l-2 4 2 4H5"
                          fill={colors.blue}
                          stroke={colors.blue}
                          strokeWidth={1.5}
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : showCheck ? (
                      <CheckmarkWhite size={20} />
                    ) : (
                      <span
                        style={{
                          fontSize: "var(--base-font-size-small)",
                          fontWeight: day.isToday ? 700 : 500,
                          color: day.isToday ? colors.blue : colors.white,
                        }}
                      >
                        {day.dayNumber}
                      </span>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        ))}

        {/* Close Calendar */}
        <button
          type="button"
          onClick={onClose}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            margin: "24px auto 0",
            padding: "10px 20px",
            borderRadius: 20,
            background: colors.whiteSubtle,
            border: "none",
            cursor: "pointer",
            color: colors.white,
          }}
        >
          <Chevron dir="up" />
          <span style={{ fontSize: "var(--base-font-size-xs)", fontWeight: 600 }}>
            Close Calendar
          </span>
        </button>
      </div>
    </div>
  );
};

export default MonthStreakCalendar;
